import type { ReactNode } from "react";
import { T, useT } from "../i18n";
import { Fehler } from "../daten/klient";
import { useSitzung } from "../schale/Sitzung";
import { tagVon, uhrVon } from "../daten/vorbereitung";
import { zahlAnzeige } from "../daten/zahlfeld";
import type { Antwort, Vorbereitung } from "../daten/vorbereitung";
import { Fotogruppen, Fragefotos, fotosVerteilen } from "./Fotogruppen";
import { useBilder } from "../daten/fotos";
import type { Blickwinkel } from "../daten/typen";
import "../stile/vorbereitung.css";

/* ══════════════════════════════════════════════════════════════════
   Was Vorbereitung, Übergabe und Startseite teilen (Migration 0107).

   Vier Stücke, und jedes steht an mehr als einer Stelle:

     Tankwahl            Übergabe am Schirm und am Handy
     Mangelband          Übergabe, Handy-Protokoll, Vorbereitung
     Vorbereitungsblick  Übergabe am Schirm und am Handy
     Wer                 Startseite, Tagesliste, Vorbereitung

   Wer eines davon ein zweites Mal baut, hat beim nächsten Wunsch zwei
   Stellen, die auseinanderlaufen.
   ══════════════════════════════════════════════════════════════════ */

/* ══════════════ Tankwahl ══════════════
   Der Tank als **Segmentwahl**, nicht als Zahlenfeld (Durchgang AD1).
   Niemand liest einen Tank auf das Prozent genau ab; wer „60" eintippt,
   hat geschätzt und so getan, als wäre es gemessen. Fünf Stufen sind das,
   was die Nadel hergibt.

   **Leer heißt „nicht abgelesen"**, nicht „Tank leer" — dafür gibt es die
   erste Stufe. Wer auf die gewählte Stufe noch einmal tippt, nimmt sie
   zurück. Ein alter Wert zwischen den Stufen (aus der Zeit vor der
   Segmentwahl) steht als eigene, gewählte Stufe daneben und wird nicht
   still gerundet: Das Protokoll sagt, was dastand. */

const TANKSTUFEN: { prozent: number; wort: string }[] = [
  { prozent: 0, wort: "uebergabe.tankstufe.leer" },
  { prozent: 25, wort: "uebergabe.tankstufe.viertel" },
  { prozent: 50, wort: "uebergabe.tankstufe.halb" },
  { prozent: 75, wort: "uebergabe.tankstufe.dreiviertel" },
  { prozent: 100, wort: "uebergabe.tankstufe.voll" },
];

export function Tankwahl({ wert, setze, gross }: {
  /** Prozent als Zeichenkette; `""` heißt nicht abgelesen. */
  wert: string;
  setze: (neu: string) => void;
  /** Am Handy: höher und breiter. */
  gross?: boolean;
}) {
  const t = useT();
  const zahl = wert.trim() === "" ? null : Number(wert);
  const zwischen = zahl !== null && Number.isFinite(zahl) && !TANKSTUFEN.some((s) => s.prozent === zahl);

  return (
    <div className={`tankwahl${gross ? " gross" : ""}`} role="group" aria-label={t("uebergabe.tankstufe.schild")}>
      {TANKSTUFEN.map((s) => {
        const an = zahl === s.prozent;
        return (
          <button
            key={s.prozent}
            type="button"
            aria-pressed={an}
            aria-label={t("uebergabe.tankstufe.wasSchild", { stufe: t(s.wort) })}
            onClick={() => setze(an ? "" : String(s.prozent))}
          >
            {t(s.wort)}
          </button>
        );
      })}
      {zwischen ? (
        <button type="button" aria-pressed onClick={() => setze("")}>
          {t("uebergabe.tankstufe.anders", { prozent: wert })}
        </button>
      ) : null}
    </div>
  );
}

/* ══════════════ Wann ══════════════ */

/* „heute 07:30", „morgen 07:30", „am 05.10. 07:30", „seit 13.10." — steht
   seit dem 03.10.2026 in `daten/wann.ts`, damit es ohne React prüfbar ist. */
export { wannText } from "../daten/wann";

/** `16.09. 14:20` — für „vorbereitet von …". */
export function zeitpunktText(iso: string): string {
  return iso ? `${tagVon(iso)} ${uhrVon(iso)}` : "";
}

/* ══════════════ Fehler ══════════════
   Die Schlüssel aus `routen/vorbereitung.ts` und `dienste/aufgaben.ts`,
   übersetzt an einer Stelle — mit dem Namen dessen, der schon genommen hat. */

export function aufgabenFehler(e: unknown): { pfad: string; werte?: Record<string, string | number>; bestaetigbar?: true } {
  if (!(e instanceof Fehler)) return { pfad: "fehler.unbekannt" };
  /* **Ein Hinweis, kein Nein** (D-47, Entscheidung E3): Fehlt der Schein,
     sagt die Zeile es und bietet „Trotzdem nehmen". */
  if (e.schluessel.startsWith("aufgabe.qualifikation_")) {
    return { pfad: e.textPfad, werte: e.werte, bestaetigbar: true };
  }
  switch (e.schluessel) {
    case "aufgabe.schon_genommen":
      return { pfad: "vorbereitung.fehlerSchonGenommen", werte: { wer: String(e.daten["wer"] ?? "") } };
    case "aufgabe.erledigt": return { pfad: "vorbereitung.fehlerErledigt" };
    case "aufgabe.nur_feste_maschine": return { pfad: "vorbereitung.fehlerNurFesteMaschine" };
    case "vorbereitung.schon_uebergeben": return { pfad: "vorbereitung.fehlerSchonUebergeben" };
    case "eingabe.antworten": return { pfad: "vorbereitung.fehlerAntworten" };
    default: return { pfad: e.textPfad };
  }
}

/* ══════════════ Wer ══════════════
   Wer die Aufgabe hat. Dazu der eine Knopf, der gerade geht: übernehmen,
   wenn sie niemandem gehört, zurück in den Pool, wenn sie einem gehört. Die
   Rechte rechnet der Server (`darfNehmen`); hier steht nur, was er erlaubt.

   **Der eingeplante Bediener hat sie, ohne zu nehmen** (Pool `bediener`,
   `dienste/aufgaben.ts`). Geprüft am 17.09.2026: Der Fahrer sah in der Liste
   „ICH" und im Blatt dahinter „frei · Nehmen" — dieselbe Aufgabe, zwei
   Aussagen. Die Liste liest `meins` vom Server, der den Bediener kennt; diese
   Stelle kannte nur `genommen`. Jetzt steht hier, wer eingeplant ist: „ICH",
   wenn ich es bin, sonst sein Name — und für ihn selbst kein „Übernehmen",
   denn seine ist sie schon. Wer lenkt, kann sie ihm weiter abnehmen.

   ── „frei" ist weggefallen — 18.09.2026 ───────────────────────────

   Luca, beim Durchgang am Telefon: *„Da muss nur das mit dem ‚frei' ein
   bisschen anders gemacht werden."* Hier standen zwei Dinge nebeneinander,
   die dasselbe sagten: der offene Ring mit dem Wort „frei" **und** daneben
   der Knopf „Nehmen". Zwei Träger für eine Aussage, und der erste davon ein
   Zustand, der keiner ist — „frei" ist keine Lage, in der etwas steckt,
   sondern eine Einladung.

   > **Gehört sie jemandem, steht sein Name da. Gehört sie niemandem, steht
   > da der Griff.** Ein Name mit gefülltem Punkt heißt „vergeben", eine
   > Zeile ohne Namen heißt „offen" — und der Griff daneben sagt in einem
   > Wort, was zu tun ist (28-designkonzept.md: Aktion ist Fläche, Zustand
   > ist Punkt).

   Wer nicht nehmen darf, sähe dann gar nichts — und eine Zeile, die zu
   ihrer Zuständigkeit schweigt, ist schlechter als eine, die „offen" sagt.
   Für ihn bleibt darum die Marke mit dem **offenen Ring**: dieselbe Form
   wie die mittlere Ampel, weil es dieselbe Aussage ist — hier muss noch
   jemand hinsehen. */

export function Wer({
  genommen, meins, bediener = [], darfNehmen, darfZurueckgeben, nehmen, zurueckgeben, laeuft, gross, ohneKnopf,
}: {
  /**
   * Nur, wer sie hat — ohne „Nehmen" und „Zurück" (17.09.2026). Am Telefon
   * übernimmt der Einsatz beides in einem Griff (`POST /handy/uebernehmen`),
   * und wer die Vorbereitung beantwortet, hat sie damit genommen.
   */
  ohneKnopf?: boolean;
  genommen: { name: string } | null;
  meins: boolean;
  /** Wer an der Position fährt. Leer: eine Maschine ohne Bediener. */
  bediener?: { id: string; name: string }[];
  darfNehmen: boolean;
  darfZurueckgeben: boolean;
  nehmen: () => void;
  zurueckgeben: () => void;
  laeuft?: boolean;
  /** Am Handy: 48 px hohe Knöpfe. */
  gross?: boolean;
}) {
  const t = useT();
  const { ich } = useSitzung();
  const eingeplant = !genommen && bediener.length > 0;
  const ichFahre = eingeplant && (meins || bediener.some((b) => b.id === ich?.id));
  const hat = !!genommen || eingeplant;
  const meinsZeigen = genommen ? meins : ichFahre;
  const name = genommen ? (meins ? t("aufgaben.ich") : genommen.name)
    : eingeplant ? (ichFahre ? t("aufgaben.ich") : bediener.map((b) => b.name).join(", "))
      : "";

  /* Der eine Griff, der gerade geht. „Übernehmen" trägt die kräftigere
     Kante: An einer Zeile ohne Namen ist er die ganze Aussage. Gefüllt in
     Hausgelb wird er nicht — die Fläche gehört dem einen Hauptknopf der
     Maske (28-designkonzept.md). */
  const griff = ohneKnopf ? null : darfNehmen && !ichFahre ? (
    <button type="button" className="aufgabe-knopf stark" disabled={laeuft} onClick={nehmen}>
      {t("aufgaben.nehmen")}
    </button>
  ) : darfZurueckgeben ? (
    <button type="button" className="aufgabe-knopf leise" disabled={laeuft} onClick={zurueckgeben}>
      {t("aufgaben.zurueck")}
    </button>
  ) : null;

  return (
    <span className={`aufgabe-wer${gross ? " gross" : ""}`}>
      {hat ? (
        <span className={`aufgabe-wer-name${meinsZeigen ? " meins" : ""}`}>
          <span className="pkt gut" aria-hidden="true" />
          {name}
        </span>
      ) : griff ? null : (
        <span className="aufgabe-wer-name offen">
          <span className="pkt acht" aria-hidden="true" />
          {t("aufgaben.offen")}
        </span>
      )}
      {griff}
    </span>
  );
}

/* ══════════════ Mangel ══════════════
   Oben in der Übergabe, nicht irgendwo darunter (Luca, 16.09.2026): Die
   Übergabe ist nicht gesperrt, aber wer übergibt, soll es vorher gelesen
   haben. */

/**
 * Welche Antworten den Mangel ausmachen — ein „Nein" oder eine Auswahl, deren
 * Möglichkeit als Mangel markiert war (Migration 0114). Dieselbe Rechnung wie
 * `has_defect` in der Datenbank; sie steht hier einmal, damit Band und Blick
 * nicht zwei verschiedene Listen zeigen.
 */
export function mangelAntworten(vorbereitung: Vorbereitung): Antwort[] {
  return vorbereitung.antworten.filter(
    (a) => (a.art === "ja_nein" && a.wert === false) || (a.art === "auswahl" && a.mangel),
  );
}

export function Mangelband({ vorbereitung, knopf }: { vorbereitung: Vorbereitung; knopf?: ReactNode }) {
  const t = useT();
  if (!vorbereitung.mangel) return null;
  const nein = mangelAntworten(vorbereitung);
  const erledigt = vorbereitung.mangelErledigt;
  return (
    <div className={`mangelband${erledigt ? " erledigt" : ""}`} role={erledigt ? undefined : "alert"}>
      <span className={`pkt ${erledigt ? "gut" : "stopp"}`} aria-hidden="true" />
      <div className="mangelband-text">
        <b>{t("vorbereitung.mangelTitel")}</b>
        {/* Eine leere Liste zeichnet keinen leeren Kasten. Bis zum 17.09.2026
            kam sie beim Techniker immer leer an: Der Preisriegel der API
            strich `wert` aus jeder Antwort (`mitte/rechte.ts`). Leer sein kann
            sie jetzt nur noch mit einem Stand aus der Mappe von davor. */}
        {nein.length ? (
          <ul>
            {/* Bei einer Auswahl sagt erst die gewählte Möglichkeit, was fehlt —
                „Zustand des Korbs" allein ist keine Auskunft. */}
            {nein.map((a) => (
              <li key={a.frageId}>
                {a.art === "auswahl" && typeof a.wert === "string"
                  ? t("vorbereitung.auswahl.mangelZeile", { frage: a.text, wert: a.wert })
                  : a.text}
              </li>
            ))}
          </ul>
        ) : null}
        <span className="leise">
          {erledigt
            ? t("vorbereitung.mangelErledigtVon", { name: erledigt.name, wann: zeitpunktText(erledigt.am) })
            : t("vorbereitung.mangelSatz")}
        </span>
        {knopf}
      </div>
    </div>
  );
}

/* ══════════════ Blick ══════════════
   Die Antworten und Fotos einer Vorbereitung, wie die Übergabe sie liest.
   **Die Fotos werden dort nicht neu gemacht** — sie stehen hier, und wer
   etwas Neues sieht, ergänzt ein weiteres Foto im Protokoll. */

function Antwortwert({ a }: { a: Antwort }) {
  const t = useT();
  if (a.wert === null) return <span className="leise">{t("allgemein.strich")}</span>;
  if (a.art === "ja_nein") {
    return (
      <span className={`antwort-jn${a.wert ? " ja" : " nein"}`}>
        {t(a.wert ? "vorbereitung.ja" : "vorbereitung.nein")}
      </span>
    );
  }
  /* Eine Auswahl liest sich wie ein Wort und nicht wie ein Messwert — und wenn
     die gewählte Möglichkeit ein Mangel ist, steht sie so da wie ein „Nein". */
  if (a.art === "auswahl") {
    return <span className={`antwort-jn${a.mangel ? " nein" : " ja"}`}>{String(a.wert)}</span>;
  }
  /* Eine Zahl steht deutsch da — `1.284,5` und nicht `1284.5`. */
  return <span className="antwort-wert">{typeof a.wert === "number" ? zahlAnzeige(a.wert) : String(a.wert)}</span>;
}

export function Vorbereitungsblick({ vorbereitung, belegungId, fotosZeigen = true, pflicht }: {
  vorbereitung: Vorbereitung | null;
  /** Woran die Fotos hängen — ohne sie bleibt der Fotoblock leer. */
  belegungId?: string;
  fotosZeigen?: boolean;
  /**
   * Die Pflichtfotos der Vorbereitung dieses Geräts (0173) — dann stehen
   * auch die leeren Plätze da. Ohne sie nur die, die ein Bild haben.
   */
  pflicht?: readonly Blickwinkel[];
}) {
  const t = useT();
  /* **Die Fotos kommen nicht mit der Vorbereitung** (Welle 2, 18.09.2026):
     Sie sind Zeilen in `photo`, gelesen über `GET /fotos`. */
  const bilder = useBilder(fotosZeigen && belegungId ? { belegung: belegungId, kontext: "vorbereitung" } : null);
  /* **Jedes Bild bei dem, wofür es gemacht wurde** (L1, 25.09.2026): unter
     seiner Prüffrage, auf seinem Pflichtplatz, sonst unter „Weitere". Bis
     dahin stand hier eine flache Reihe. */
  const verteilung = fotosVerteilen(bilder.bilder, {
    fragen: (vorbereitung?.antworten ?? []).map((a) => ({ id: a.frageId, text: a.text })),
    ...(pflicht ? { pflicht } : {}),
  });
  if (!vorbereitung?.erledigt) {
    return (
      <div className="vorb-blick fehlt">
        <b>{t("vorbereitung.blickFehlt")}</b>
        <span className="leise">{t("vorbereitung.blickFehltSatz")}</span>
      </div>
    );
  }
  return (
    <div className="vorb-blick">
      {vorbereitung.antworten.length ? (
        <dl className="vorb-antworten">
          {vorbereitung.antworten.map((a) => (
            <div
              key={a.frageId}
              className={(a.art === "ja_nein" && a.wert === false) || (a.art === "auswahl" && a.mangel)
                ? "nein" : undefined}
            >
              <dt>{a.text}</dt>
              <dd><Antwortwert a={a} /></dd>
              {fotosZeigen ? <Fragefotos bilder={verteilung.jeFrage.get(a.frageId)} /> : null}
            </div>
          ))}
        </dl>
      ) : null}
      {vorbereitung.notiz ? <p className="vorb-notiz">{vorbereitung.notiz}</p> : null}
      {fotosZeigen ? <Fotogruppen verteilung={verteilung} leer="vorbereitung.blickFotosKeine" /> : null}
      <span className="vorb-wer">
        <T k={vorbereitung.beiUebergabe ? "vorbereitung.erledigtBeiUebergabe" : "vorbereitung.erledigtVon"} werte={{
          name: vorbereitung.erledigt.name || t("allgemein.strich"),
          wann: zeitpunktText(vorbereitung.erledigt.am),
        }} />
      </span>
    </div>
  );
}
